/**
 * C1 — полки «Похожие» / «Вы смотрели» на PDP (I/R/B через shared; B импортирует этот модуль).
 */

import { state, BASE_PATH } from './state';
import { escapeHtml, formatPrice, formatMoney, getConfig, normalizeProduct, toWebpSrc } from './helpers';
import { ICONS } from './icons';
import { initReviewsBlock } from './reviews';
import { renderGridBadgeStack } from './product-badges';
import type { Product, ProductRaw } from './types';

const HOST_ID = 'pdp-cross-sell';
const DISPLAY_LIMIT = 8;
const RECENT_MAX = 12;

interface CardsResponse {
  products?: ProductRaw[];
}

function recentStorageKey(): string {
  const sf = getConfig('storefront', 'i') as string;
  return `torgirt-${sf}-recent`;
}

function loadRecentArticles(): string[] {
  try {
    const raw = localStorage.getItem(recentStorageKey());
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((a): a is string => typeof a === 'string' && a.trim() !== '');
  } catch {
    return [];
  }
}

function saveRecentArticles(list: string[]): void {
  try {
    localStorage.setItem(recentStorageKey(), JSON.stringify(list.slice(0, RECENT_MAX)));
  } catch {
    /* ignore quota */
  }
}

function rememberRecent(article: string): void {
  const a = article.trim();
  if (!a) return;
  const list = loadRecentArticles().filter((x) => x !== a);
  list.unshift(a);
  saveRecentArticles(list);
}

function shelfPicture(src: string, alt: string): string {
  const webp = toWebpSrc(src);
  if (!webp) {
    return `<img src="${escapeHtml(src)}" alt="${escapeHtml(alt)}" loading="lazy" decoding="async" />`;
  }
  return `<picture><source srcset="${escapeHtml(webp)}" type="image/webp" /><img src="${escapeHtml(src)}" alt="${escapeHtml(alt)}" loading="lazy" decoding="async" /></picture>`;
}

function renderShelfCard(product: Product): string {
  const stock = product.stock ?? 0;
  const firstImage = product.images?.[0] ?? '';
  const href = `${BASE_PATH}/product/${encodeURIComponent(product.article)}`;

  const actionHtml = `<a href="${escapeHtml(href)}" class="btn btn-outline btn-sm pdp-cross-sell__more">Подробнее</a>`;

  return `
    <article class="product-card pdp-cross-sell__card" data-article="${escapeHtml(product.article)}">
      <div class="product-image">
        <a href="${escapeHtml(href)}" class="pdp-cross-sell__media">
          ${firstImage ? shelfPicture(firstImage, product.name) : `<div class="placeholder">${ICONS.packageMd}</div>`}
        </a>
        ${renderGridBadgeStack(product.badges ?? [], product.oldPrice ?? null, product.price, stock)}
      </div>
      <div class="product-content">
        <h3 class="product-title">
          <a href="${escapeHtml(href)}" class="product-title-text">${escapeHtml(product.name)}</a>
        </h3>
        <div class="price-line">
          <span class="price-value">${formatPrice(product.price)}</span><span class="price-unit">\u00A0₽</span>
          ${product.oldPrice && product.oldPrice > product.price ? `<span class="old-price">${formatMoney(product.oldPrice)}</span>` : ''}
        </div>
        ${actionHtml}
      </div>
    </article>
  `;
}

function renderSection(title: string, products: Product[]): string {
  if (!products.length) return '';
  const cards = products.slice(0, DISPLAY_LIMIT).map(renderShelfCard).join('');
  return `
    <section class="pdp-cross-sell__section">
      <h2 class="pdp-cross-sell__title">${escapeHtml(title)}</h2>
      <div class="pdp-cross-sell__track">${cards}</div>
    </section>
  `;
}

async function fetchSimilar(article: string): Promise<Product[]> {
  const base = getConfig('ajaxProductSimilarUrl', '') as string;
  if (!base) return [];
  const url = `${base}?article=${encodeURIComponent(article)}`;
  const res = await fetch(url);
  if (!res.ok) return [];
  const data = (await res.json()) as CardsResponse;
  if (!Array.isArray(data.products)) return [];
  return data.products.map(normalizeProduct);
}

async function fetchRecentCards(articles: string[]): Promise<Product[]> {
  if (!articles.length) return [];
  const base = getConfig('ajaxProductCardsUrl', '') as string;
  if (!base) return [];
  const url = `${base}?articles=${encodeURIComponent(articles.join(','))}`;
  const res = await fetch(url);
  if (!res.ok) return [];
  const data = (await res.json()) as CardsResponse;
  if (!Array.isArray(data.products)) return [];
  return data.products.map(normalizeProduct);
}

export function removePdpCrossSell(): void {
  document.getElementById(HOST_ID)?.remove();
  const dynamicReviews = document.getElementById('reviews-block');
  if (dynamicReviews?.dataset.pdpReviewsDynamic === '1') {
    dynamicReviews.remove();
  }
}

export function ensurePdpReviewsBlock(productId: number): void {
  if (getConfig('modulesEnabled')?.reviews !== true || productId <= 0) {
    return;
  }

  let block = document.getElementById('reviews-block');
  const productsEl = document.getElementById('products');
  if (block && productsEl?.contains(block)) {
    block.remove();
    block = null;
  }

  if (!block) {
    block = document.createElement('section');
    block.id = 'reviews-block';
    block.className = 'reviews-section reviews-section--placeholder';
    block.dataset.pdpReviewsDynamic = '1';
    block.innerHTML = '<h3 class="reviews-section__title">Отзывы</h3><p class="reviews-section__loading">Загрузка отзывов…</p>';
  }

  block.dataset.productId = String(productId);
  const anchor = document.getElementById(HOST_ID) ?? document.getElementById('products');
  if (anchor) {
    anchor.insertAdjacentElement('afterend', block);
  }

  void initReviewsBlock(block);
}

let mountGen = 0;

function markShelfClampedTitles(root: HTMLElement): void {
  root.querySelectorAll('.product-title-text').forEach((el) => {
    const parent = el.closest('.product-title');
    if (!parent) return;
    parent.classList.toggle(
      'is-clamped',
      el.scrollHeight > el.clientHeight + 1,
    );
  });
}

export async function mountPdpCrossSell(): Promise<void> {
  const p = state.productPage;
  if (!p?.article) {
    removePdpCrossSell();
    return;
  }

  const gen = ++mountGen;
  const article = p.article;
  const recentForDisplay = loadRecentArticles().filter((a) => a !== article);

  const [similar, recentProducts] = await Promise.all([
    fetchSimilar(article),
    fetchRecentCards(recentForDisplay),
  ]);

  rememberRecent(article);

  if (gen !== mountGen) return;

  const similarFiltered = similar.filter((item) => item.article !== article);
  const recentFiltered = recentProducts.filter((item) => item.article !== article);

  const html =
    renderSection('Похожие товары', similarFiltered) +
    renderSection('Вы смотрели', recentFiltered);

  if (!html.trim()) {
    document.getElementById(HOST_ID)?.remove();
    if (p.id) ensurePdpReviewsBlock(p.id);
    return;
  }

  const productsEl = document.getElementById('products');
  if (!productsEl) return;

  let host = document.getElementById(HOST_ID);
  if (!host) {
    host = document.createElement('div');
    host.id = HOST_ID;
    host.className = 'pdp-cross-sell';
    productsEl.insertAdjacentElement('afterend', host);
  }
  host.innerHTML = html;

  markShelfClampedTitles(host);
  requestAnimationFrame(() => markShelfClampedTitles(host));

  if (p.id) ensurePdpReviewsBlock(p.id);
}
